import { useRef, useState } from 'react';
import type { BuildPriority } from '@hangar/shared';
import { toast } from 'sonner';
import { ApiError } from '@/api/client';
import type { useBuildMutations } from '@/api/hooks';
import { showErrorToast } from '@/lib/toasts';

type Mutations = ReturnType<typeof useBuildMutations>;

const fail = (e: unknown) => showErrorToast(e instanceof ApiError ? { title: e.toastTitle, description: `${e.code} · ${e.message}` } : { title: 'Something went wrong' });

/** The cancel confirmation dialog: its open state, the optional reason and the request. */
export function useCancelAction(cancel: Mutations['cancel']) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [reason, setReason] = useState('');
  const onCancel = async () => {
    try {
      await cancel.mutateAsync(reason.trim() || undefined);
      toast.success('Cancel requested', { description: 'The provider confirms the cancellation asynchronously.' });
      setConfirmCancel(false);
    } catch (e) {
      fail(e);
    }
  };
  return { confirmCancel, setConfirmCancel, reason, setReason, onCancel };
}

/** Retry with one idempotency key per attempt: a new key only after the retry went through. */
export function useRetryAction(retry: Mutations['retry']) {
  const retryKey = useRef(crypto.randomUUID());
  return async () => {
    try {
      const child = await retry.mutateAsync(retryKey.current);
      retryKey.current = crypto.randomUUID();
      toast.success(`${child.ref} retried`, { description: `attempt ${child.attempt} · same snapshot and build number` });
    } catch (e) {
      fail(e);
    }
  };
}

/** Changes the queue priority; choosing the current one does nothing. */
export function usePriorityAction(priority: Mutations['priority'], current: BuildPriority) {
  return async (p: BuildPriority) => {
    if (p === current) return;
    try {
      await priority.mutateAsync({ priority: p });
      toast.success(`Priority set to ${p}`);
    } catch (e) {
      fail(e);
    }
  };
}
